'use client';
import { useEffect, useRef, useState } from 'react';
import { Target, Users, ShieldCheck, Rocket, ArrowRight, FlaskConical } from 'lucide-react';
import { useT } from '../../i18n';

interface ServiceModule {
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  problem: string;
  solution: string;
  result: string;
  href?: string;
}

const ServicesSection = () => {
  const t = useT();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const modules: ServiceModule[] = [
    {
      icon: Target,
      eyebrow: t('services.m1.eyebrow'),
      title: t('services.m1.title'),
      problem: t('services.m1.problem'),
      solution: t('services.m1.solution'),
      result: t('services.m1.result'),
    },
    {
      icon: Users,
      eyebrow: t('services.m2.eyebrow'),
      title: t('services.m2.title'),
      problem: t('services.m2.problem'),
      solution: t('services.m2.solution'),
      result: t('services.m2.result'),
    },
    {
      icon: ShieldCheck,
      eyebrow: t('services.m3.eyebrow'),
      title: t('services.m3.title'),
      problem: t('services.m3.problem'),
      solution: t('services.m3.solution'),
      result: t('services.m3.result'),
    },
    {
      icon: Rocket,
      eyebrow: t('services.m4.eyebrow'),
      title: t('services.m4.title'),
      problem: t('services.m4.problem'),
      solution: t('services.m4.solution'),
      result: t('services.m4.result'),
    },
    {
      icon: FlaskConical,
      eyebrow: t('services.m5.eyebrow'),
      title: t('distill.card.title'),
      problem: t('services.m5.problem'),
      solution: t('distill.card.desc'),
      result: t('distill.card.cta'),
      href: '/distill/',
    },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-jkd-black overflow-hidden"
    >
      {/* Background water glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-jkd-gold/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-6 border border-jkd-gold/30 rounded-full text-jkd-gold text-sm tracking-widest uppercase">
            {t('services.tag')}
          </span>
          <h2 className="section-title text-white mb-4">
            {t('services.title')}
          </h2>
          <p className="section-subtitle mx-auto">
            {t('services.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* Service Modules Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {modules.map((module, index) => {
            const Icon = module.icon;
            const inner = (
              <div
                key={module.eyebrow}
                className={`group h-full card-dark card-gold-border hover:-translate-y-1 transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-lg bg-jkd-gold/10 border border-jkd-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-jkd-gold/20 transition-colors">
                    <Icon className="w-6 h-6 text-jkd-gold" />
                  </div>
                  <div>
                    <p className="text-jkd-gold text-xs font-mono tracking-wider uppercase mb-1">
                      {module.eyebrow}
                    </p>
                    <h3 className="text-xl font-bold text-white">{module.title}</h3>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="pb-4 border-b border-jkd-gray-200/50">
                    <p className="text-gray-400 text-sm mb-1">
                      {t('services.problemLabel')}
                    </p>
                    <p className="text-gray-200">{module.problem}</p>
                  </div>
                  <div className="pb-4 border-b border-jkd-gray-200/50">
                    <p className="text-gray-400 text-sm mb-1">
                      {t('services.solutionLabel')}
                    </p>
                    <p className="text-gray-200">{module.solution}</p>
                  </div>
                  <div>
                    <p className="text-jkd-gold text-sm font-medium inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-jkd-gold" />
                      {module.result}
                    </p>
                  </div>
                </div>
              </div>
            );
            return module.href ? (
              <a key={'lnk-' + module.eyebrow} href={module.href} className='block'>
                {inner}
              </a>
            ) : (
              inner
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <a
            href="/diagnosis"
            className="group inline-flex items-center gap-3 text-jkd-gold font-bold text-lg hover:text-white transition-colors"
          >
            {t('services.cta')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
